import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import {
  Anchor, Play, Pause, FileBarChart, AlertTriangle, UserRound, Download, Plus, Search, Mail, Building2, Clock,
  Globe, MapPin, Trash2, Loader2, CheckCircle2, XCircle, Copy, Waves,
  Landmark, Link2, Compass, Trophy, Church, ListPlus, MailQuestion, Pencil, User, Phone, Combine, GitMerge, LogOut, BookOpen,
  ArrowUp, ArrowDown, ArrowUpDown, Upload, History, Folder, Settings2, FileText,
} from "lucide-react";
import { SentCell, SENT_FILTERS } from "./SentCell";
import { SentFilterBar } from "./SentFilterBar";
import { ScrapeReport } from "./ScrapeReport";
import { ScrapeSettings } from "./ScrapeSettings";
import { exportedToast } from "./ExportUndo";
import { ScheduleSelection } from "./ScheduleSelection";
import { TodayBar } from "./TodayBar";
import { WeekOverview } from "./WeekOverview";
import { LeadsImport } from "./LeadsImport";
import { Snapshots } from "./Snapshots";
import { ChangeLog } from "./ChangeLog";
import { MapToExcel } from "./MapToExcel";
import { MoveSelect } from "./MoveSelect";
import { ProfilesManager, PROFILE_ICONS } from "./ProfilesManager";
import Crm from "./Crm";
import { WorkflowGuide } from "./WorkflowGuide";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GENERIC_LOCALS = ["info", "contact", "mail", "email", "post", "welkom", "algemeen",
  "administratie", "secretariaat", "secretariat", "office", "sales", "receptie", "balie"];
const addrKind = (email) => {
  if (!email || !email.includes("@")) return null;
  const lp = email.split("@")[0].toLowerCase().replace(/[._-]/g, "");
  return GENERIC_LOCALS.some((g) => lp.startsWith(g)) ? "algemeen" : "persoonlijk";
};
const KIND_SORTS = {
  persoonlijk: ["persoonlijk", "algemeen", "geen"],
  algemeen: ["algemeen", "persoonlijk", "geen"],
  geen: ["geen", "persoonlijk", "algemeen"],
};
const KIND_LABEL = { persoonlijk: "Persoonlijk eerst", algemeen: "Algemeen eerst", geen: "Geen contact eerst" };
const MARINA_IMG =
  "https://images.unsplash.com/photo-1616946329807-cef752f80129?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxtYXJpbmElMjBhZXJpYWx8ZW58MHx8fHwxNzg4MjM4MTU3fDA&ixlib=rb-4.1.0&q=85";

const BASE_LIST = [
  {
    id: "watersport", label: "Watersportverenigingen", short: "Watersport",
    icon: Waves, hasDirectory: true,
    priority: "penningmeester → voorzitter → secretaris → info",
    example: "wsvvoorbeeld.nl\nhttps://www.zeilclub.nl",
    suggestedSources: ["https://www.curlie.org/nl/Sport/Watersport/Verenigingen"],
    suggestedFollow: true,
  },
  {
    id: "sport", label: "Sportverenigingen", short: "Sport",
    icon: Trophy, hasDirectory: false,
    priority: "penningmeester → ledenadministratie → voorzitter → secretaris → info",
    example: "svvoorbeeld.nl\nhttps://www.voetbalclub.nl",
    suggestedSources: ["mhcepe.nl", "hcdenbosch.nl", "amsterdamhc.nl"],
    suggestedFollow: false,
  },
  {
    id: "vve", label: "VvE's", short: "VvE's",
    icon: Landmark, hasDirectory: false,
    priority: "beheerder → beheer → bestuur → voorzitter → secretaris → info",
    example: "vvevoorbeeld.nl\nhttps://www.mijnvve.nl",
    suggestedSources: ["vvebelang.nl", "newomij.nl", "vve-beheer.nl"],
    suggestedFollow: false,
  },
  {
    id: "kerk", label: "Kerkelijke besturen", short: "Kerken",
    icon: Church, hasDirectory: false,
    priority: "scriba → kerkenraad → voorzitter → secretaris → predikant → info",
    example: "kerkvoorbeeld.nl\nhttps://www.pkn-plaats.nl",
    suggestedSources: ["pkn-amersfoort.nl", "pgkampen.nl", "protestantsegemeente-enschede.nl"],
    suggestedFollow: false,
  },
];
BASE_LIST.push({
  id: "algemeen", label: "Algemeen", short: "Algemeen", icon: Folder, hasDirectory: false,
  priority: "secretariaat → bestuur → info → contact",
  example: "voorbeeld.nl\nhttps://www.vereniging.nl", suggestedSources: [], suggestedFollow: false,
});
export const BASE_CFG = Object.fromEntries(BASE_LIST.map((b) => [b.id, b]));
const DEFAULT_PROFILES = BASE_LIST.slice(0, 4).map((b) => ({ ...b, base: b.id }));
const toCfg = (p) => ({
  ...(BASE_CFG[p.base] || BASE_CFG.algemeen),
  id: p.id, label: p.label, short: p.short || p.label, base: p.base,
  icon: PROFILE_ICONS[p.icon] || Folder, iconName: p.icon, builtin: p.builtin, count: p.count,
  hasDirectory: p.id === "watersport",
  suggestedSources: p.builtin ? (BASE_CFG[p.base] || BASE_CFG.algemeen).suggestedSources : [],
});

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div
      data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}
      className="bg-white border border-slate-200 rounded-lg p-6 flex flex-col gap-3"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold tracking-[0.1em] uppercase text-slate-500">
          {label}
        </span>
        <Icon className={`w-5 h-5 ${accent || "text-slate-400"}`} strokeWidth={1.5} />
      </div>
      <span className="font-display text-4xl font-bold tracking-tight text-slate-900">
        {value}
      </span>
    </div>
  );
}

function EditableCell({ value, onSave, testid, placeholder, icon: Icon, width = "w-36" }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const start = () => { setVal(value || ""); setEditing(true); };
  const save = async () => { await onSave(val.trim()); setEditing(false); };
  if (editing) {
    return (
      <div className="relative z-20 flex items-center gap-1.5 bg-white rounded-md pr-1 shadow-sm w-max">
        <Input
          data-testid={`${testid}-input`}
          autoFocus
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
          placeholder={placeholder}
          className={`h-8 text-sm ${width}`}
        />
        <Button data-testid={`${testid}-save`} size="sm" onClick={save} className="h-8 px-2 shrink-0 bg-emerald-600 hover:bg-emerald-700">
          <CheckCircle2 className="w-4 h-4" strokeWidth={2} />
        </Button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 group">
      {value ? (
        <span data-testid={testid} className="inline-flex items-center gap-1">
          {Icon && <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" strokeWidth={1.5} />} {value}
        </span>
      ) : <span className="text-slate-300">—</span>}
      <button
        data-testid={`${testid}-edit`}
        onClick={start}
        className="opacity-40 md:opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity text-slate-400 hover:text-slate-700"
        title={value ? "Aanpassen" : "Invullen"}
      >
        {value ? <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} /> : <Plus className="w-4 h-4" strokeWidth={1.5} />}
      </button>
    </div>
  );
}

function boardMembers(board) {
  if (Array.isArray(board)) return board;
  if (board) return [{ role: "", name: board }];
  return [];
}

function BoardCell({ club, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const members = boardMembers(club.board);
  const start = () => {
    setVal(members.map((m) => (m.role ? `${m.role}: ${m.name}` : m.name)).join("\n"));
    setEditing(true);
  };
  const save = async () => { await onSave(val); setEditing(false); };
  if (editing) {
    return (
      <div className="flex flex-col gap-1.5">
        <Textarea
          data-testid={`board-edit-input-${club.domain}`}
          autoFocus
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Escape") setEditing(false); }}
          placeholder={"Penningmeester: Jan de Vries\nSecretaris: Piet Bakker"}
          className="min-h-[72px] text-sm w-60 font-mono"
        />
        <div className="flex gap-1.5">
          <Button data-testid={`board-save-${club.domain}`} size="sm" onClick={save} className="h-7 px-2.5 bg-emerald-600 hover:bg-emerald-700">
            <CheckCircle2 className="w-4 h-4 mr-1" strokeWidth={2} /> Opslaan
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-7 px-2 text-slate-400">
            <XCircle className="w-4 h-4" strokeWidth={2} />
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2 group">
      {members.length ? (
        <div className="flex flex-col gap-0.5 min-w-0" data-testid={`board-${club.domain}`}>
          {members.map((m, i) => (
            <span key={i} className="inline-flex items-center gap-1 text-slate-700">
              <User className="w-3.5 h-3.5 text-slate-400 shrink-0" strokeWidth={1.5} />
              {m.role ? <span><span className="text-slate-500">{m.role}:</span> {m.name}</span> : m.name}
            </span>
          ))}
        </div>
      ) : <span className="text-slate-300">—</span>}
      <button
        data-testid={`board-edit-${club.domain}`}
        onClick={start}
        className="opacity-40 md:opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity text-slate-400 hover:text-slate-700 mt-0.5 shrink-0"
        title={members.length ? "Bestuursleden aanpassen" : "Bestuursleden invullen"}
      >
        {members.length ? <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} /> : <Plus className="w-4 h-4" strokeWidth={1.5} />}
      </button>
    </div>
  );
}

const Login = ({ onSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("mjp_token", data.token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
      onSuccess();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Inloggen mislukt");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b1220] text-slate-100 flex items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #1d4ed8 0, transparent 40%), radial-gradient(circle at 80% 70%, #0ea5e9 0, transparent 45%)" }} />
      <div className="relative w-full max-w-md">
        <div className="flex flex-col items-center mb-6 text-center">
          <img src="https://customer-assets-eiarnc6j.emergentagent.net/job_nl-aquatic-harvest/artifacts/993xvmcb_Logo%20Meerjarenplanonline.nl.png" alt="Meerjarenplan Online" className="h-14 mb-4 bg-white rounded-lg p-2" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <h1 className="font-display text-2xl font-bold">Lead- &amp; CRM-omgeving</h1>
          <p className="text-sm text-slate-400 mt-1 max-w-xs">Besloten werkomgeving. Uitsluitend voor geautoriseerde admin- en staf­leden van Meerjarenplan Online.</p>
        </div>
        <form onSubmit={submit} className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 flex flex-col gap-4">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">E-mail</label>
            <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@..." className="bg-white/10 border-white/15 text-white placeholder:text-slate-500" autoFocus />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Wachtwoord</label>
            <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="bg-white/10 border-white/15 text-white placeholder:text-slate-500" />
          </div>
          {err && <p data-testid="login-error" className="text-sm text-red-400">{err}</p>}
          <Button data-testid="login-submit" type="submit" disabled={loading} className="rounded-full bg-[#FF4500] hover:bg-[#E63E00] text-white h-11">
            {loading ? "Bezig..." : "Inloggen"}
          </Button>
          <p className="text-[11px] text-slate-500 text-center">Toegang wordt gelogd. Onbevoegd gebruik is niet toegestaan.</p>
        </form>
      </div>
    </div>
  );
};

export default function App() {
  const [authed, setAuthed] = useState(null);
  useEffect(() => {
    const t = localStorage.getItem("mjp_token");
    if (!t) { setAuthed(false); return; }
    axios.defaults.headers.common["Authorization"] = `Bearer ${t}`;
    axios.get(`${API}/auth/me`).then(() => setAuthed(true)).catch(() => {
      localStorage.removeItem("mjp_token");
      delete axios.defaults.headers.common["Authorization"];
      setAuthed(false);
    });
  }, []);
  if (authed === null) return <div className="min-h-screen grid place-items-center bg-[#0b1220] text-slate-400">Laden…</div>;
  if (!authed) return <Login onSuccess={() => setAuthed(true)} />;
  return <AppMain onLogout={() => {
    localStorage.removeItem("mjp_token");
    delete axios.defaults.headers.common["Authorization"];
    setAuthed(false);
  }} />;
}

function AppMain({ onLogout }) {
  const [stats, setStats] = useState({ total_clubs: 0, clubs_with_email: 0, total_emails: 0, last_scrape: null });
  const [clubs, setClubs] = useState([]);
  const [search, setSearch] = useState("");
  const [onlyEmail, setOnlyEmail] = useState(false);
  const [noEmail, setNoEmail] = useState(false);
  const [sentSort, setSentSort] = useState(null);
  const [sentFilter, setSentFilter] = useState(null);
  const [kindSort, setKindSort] = useState(null);
  const [report, setReport] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [mapImportOpen, setMapImportOpen] = useState(false);
  const [pdfImportOpen, setPdfImportOpen] = useState(false);
  const [snapsOpen, setSnapsOpen] = useState(false);
  const [historyClub, setHistoryClub] = useState(null);
  const [exportStepSel, setExportStepSel] = useState(1);
  const [job, setJob] = useState(null);
  const [customOpen, setCustomOpen] = useState(false);
  const [customUrls, setCustomUrls] = useState("");
  const [useDirectory, setUseDirectory] = useState(true);
  const [profile, setProfile] = useState("watersport");
  const [sources, setSources] = useState(BASE_CFG.watersport.suggestedSources.join("\n"));
  const [followLinks, setFollowLinks] = useState(BASE_CFG.watersport.suggestedFollow);
  const [skipScraped, setSkipScraped] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editingCat, setEditingCat] = useState(null);
  const [editCatValue, setEditCatValue] = useState("");
  const [batchLeads, setBatchLeads] = useState([]);
  const [batchNonce, setBatchNonce] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [crmOpen, setCrmOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [crmAutoHelper, setCrmAutoHelper] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showQuickstart, setShowQuickstart] = useState(() => localStorage.getItem("mjp_quickstart_hidden") !== "1");
  const [mergePrimary, setMergePrimary] = useState(null);
  const [dupGroups, setDupGroups] = useState([]);
  const [dupOpen, setDupOpen] = useState(false);
  const [searchedSources, setSearchedSources] = useState([]);
  const [ignored, setIgnored] = useState([]);
  const [scrollW, setScrollW] = useState(0);
  const [overflowX, setOverflowX] = useState(false);
  const [colWidths, setColWidths] = useState(() => {
    try { return JSON.parse(localStorage.getItem("kielzog_colw") || "{}"); } catch (e) { return {}; }
  });
  const pollRef = useRef(null);
  const tableScrollRef = useRef(null);
  const barRef = useRef(null);

  const [profileList, setProfileList] = useState(DEFAULT_PROFILES);
  const [profilesOpen, setProfilesOpen] = useState(false);
  const loadProfiles = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/profiles`);
      if (data.length) setProfileList(data.map(toCfg));
    } catch { /* keep defaults */ }
  }, []);
  useEffect(() => { loadProfiles(); }, [loadProfiles]);
  const PROFILES = profileList;
  const cfg = PROFILES.find((p) => p.id === profile) || PROFILES[0];
  const hasDirectory = cfg.hasDirectory;
  const isSport = cfg.base === "sport";
  const noun = cfg.label.toLowerCase();

  const switchProfile = (id) => {
    const p = PROFILES.find((x) => x.id === id) || PROFILES[0];
    setProfile(id);
    setSources(p.suggestedSources.join("\n"));
    setFollowLinks(p.suggestedFollow);
    setSearch("");
    setOnlyEmail(false);
    setEditingId(null);
    setEditingCat(null);
    setSelectedIds([]);
  };

  const saveEmail = async (club) => {
    try {
      await axios.patch(`${API}/clubs/${club.id}`, { email: editValue.trim(), via: "handmatig" });
      setEditingId(null);
      setEditValue("");
      fetchClubs();
      fetchStats();
      toast.success("E-mail opgeslagen");
    } catch (e) {
      toast.error(e?.response?.status === 422 ? "Ongeldig e-mailadres" : "Opslaan mislukt");
    }
  };

  const saveCategory = async (club) => {
    try {
      await axios.patch(`${API}/clubs/${club.id}`, { category: editCatValue.trim() });
      setEditingCat(null);
      setEditCatValue("");
      fetchClubs();
      toast.success("Sport opgeslagen");
    } catch (e) {
      toast.error("Opslaan mislukt");
    }
  };

  const patchField = async (club, field, value, msg) => {
    try {
      await axios.patch(`${API}/clubs/${club.id}`, { [field]: value });
      fetchClubs();
      toast.success(msg);
    } catch (e) {
      toast.error("Opslaan mislukt");
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const startBatch = () => {
    const rows = clubs
      .filter((c) => selectedIds.includes(c.id))
      .map((c) => ({
        id: c.id,
        name: c.name,
        email: (c.emails || [])[0] || "",
        status: (c.crm || {}).status || "none",
        step: (c.crm || {}).step || 0,
        next_send_at: (c.crm || {}).next_send_at || "",
      }));
    if (rows.length === 0) return;
    setBatchLeads(rows);
    setBatchNonce((n) => n + 1);
    setCrmOpen(true);
  };

  const exportSelectionCsv = async () => {
    if (selectedIds.length === 0) return;
    const step = exportStepSel;
    try {
      const res = await axios.post(`${API}/crm/export-mails`, { ids: selectedIds, step }, {
        responseType: "blob",
      });
      const exported = Number(res.headers["x-exported"] ?? selectedIds.length);
      const skipped = Number(res.headers["x-skipped"] ?? 0);
      if (exported === 0) {
        toast.error(`Geen van de geselecteerde contacten is aan de beurt voor Mail ${step} (${skipped} overgeslagen)`);
        return;
      }
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `mails_selectie_mail${step}_${exported}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      exportedToast(API, res, step, () => { fetchClubs(); fetchStats(); });
      setSelectedIds([]);
      fetchClubs();
      fetchStats();
    } catch {
      toast.error("Export mislukt");
    }
  };

  const openMerge = () => {
    if (selectedIds.length < 2) return;
    setMergePrimary(selectedIds[0]);
    setMergeOpen(true);
  };

  const doMerge = async () => {
    try {
      await axios.post(`${API}/clubs/merge`, { primary_id: mergePrimary, ids: selectedIds });
      setMergeOpen(false);
      setSelectedIds([]);
      fetchClubs();
      fetchStats();
      toast.success("Records samengevoegd");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Samenvoegen mislukt");
    }
  };

  const mergeGroup = (group) => {
    const ids = group.clubs.map((c) => c.id);
    setSelectedIds(ids);
    setMergePrimary(ids[0]);
    setDupOpen(false);
    setMergeOpen(true);
  };

  const mergeAllDuplicates = async () => {
    if (!window.confirm(`Alle ${dupGroups.length} duplicaatgroepen automatisch samenvoegen? Per groep blijft het meest complete record (met e-mail) behouden.`)) return;
    try {
      const { data } = await axios.post(`${API}/clubs/merge-duplicates`, null, { params: { profile } });
      setDupOpen(false);
      setSelectedIds([]);
      fetchClubs();
      fetchStats();
      toast.success(`${data.groups_merged} groepen samengevoegd, ${data.records_removed} records opgeruimd`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Samenvoegen mislukt");
    }
  };

  const fetchStats = useCallback(async () => {
    const { data } = await axios.get(`${API}/stats`, { params: { profile } });
    setStats(data);
  }, [profile]);

  const fetchClubs = useCallback(async () => {
    const { data } = await axios.get(`${API}/clubs`, {
      params: { search: search || undefined, only_email: onlyEmail, profile },
    });
    setClubs(data);
  }, [search, onlyEmail, profile]);

  const emailFiltered = noEmail ? clubs.filter((c) => !((c.emails || []).length)) : clubs;
  const filteredClubs = sentFilter ? emailFiltered.filter(SENT_FILTERS[sentFilter]) : emailFiltered;
  const sentCounts = useMemo(() => Object.fromEntries(
    Object.entries(SENT_FILTERS).map(([k, fn]) => [k, clubs.filter(fn).length])), [clubs]);
  const kindRank = (c) => {
    const k = addrKind((c.emails || [])[0]);
    const order = KIND_SORTS[kindSort] || [];
    return order.indexOf(k || "geen");
  };
  const displayClubs = kindSort
    ? [...filteredClubs].sort((a, b) => kindRank(a) - kindRank(b) || (a.name || "").localeCompare(b.name || ""))
    : sentSort
    ? [...filteredClubs].sort((a, b) => {
        const ta = (a.crm || {}).last_sent_at || "";
        const tb = (b.crm || {}).last_sent_at || "";
        if (!ta && !tb) return 0;
        if (!ta) return 1;
        if (!tb) return -1;
        return sentSort === "asc" ? ta.localeCompare(tb) : tb.localeCompare(ta);
      })
    : filteredClubs;
  const toggleSentSort = () => { setKindSort(null); setSentSort((s) => (s === null ? "desc" : s === "desc" ? "asc" : null)); };
  const toggleKindSort = () => {
    setSentSort(null);
    setKindSort((s) => (s === null ? "persoonlijk" : s === "persoonlijk" ? "algemeen" : s === "algemeen" ? "geen" : null));
  };
  const patchClub = (doc) => setClubs((prev) => prev.map((x) => (x.id === doc.id ? doc : x)));
  const markReplied = (club) => markCrm(club, "replied", "gereageerd — geen vervolgmails meer");
  const markCustomer = (club) => markCrm(club, "customer", "klant geworden");
  const markPermission = async (club, status) => {
    try {
      const { data } = await axios.post(`${API}/crm/permission`, { club_id: club.id, status });
      setClubs((prev) => prev.map((c) => (c.id === club.id ? data : c)));
      toast.success(status === "granted" ? "Toestemming gegeven — klaar voor Mail 1" : "Geen toestemming — geen verdere mails");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    }
  };
  const markCrm = async (club, status, msg) => {
    const crm = club.crm || {};
    const prev = crm.status || "active";
    try {
      const { data } = await axios.post(`${API}/crm/mark`, { club_id: club.id, status });
      patchClub(data);
      toast.success(`${club.name}: ${msg}`, {
        duration: 6000,
        action: {
          label: "Ongedaan maken",
          onClick: async () => {
            const r = await axios.post(`${API}/crm/mark`, {
              club_id: club.id, status: prev, undo_reply: !crm.replied_at, undo_customer: !crm.customer_at,
            });
            patchClub(r.data);
          },
        },
      });
    } catch { toast.error("Markeren mislukt"); }
  };
  const pickToday = (filter, step) => {
    setSentFilter(filter);
    setNoEmail(false);
    if (step) setExportStepSel(step);
    setTimeout(() => document.querySelector('[data-testid="sent-filter-bar"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
  };

  const syncSnapshot = async () => {
    if (!window.confirm("Ontbrekende leads uit de ingebouwde dataset toevoegen aan deze database? Bestaande leads blijven ongemoeid.")) return;
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/admin/sync-snapshot`);
      toast.success(`${data.added} leads toegevoegd — totaal nu ${data.total}.`);
      fetchClubs(); fetchStats(); fetchSources();
    } catch (e) {
      toast.error("Synchroniseren mislukt");
    } finally {
      setSyncing(false);
    }
  };

  const scrollToSources = () => {
    document.querySelector('[data-testid="sources-panel"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const fetchSources = useCallback(async () => {
    const { data } = await axios.get(`${API}/sources`, { params: { profile } });
    setSearchedSources(data);
  }, [profile]);

  const fetchIgnored = useCallback(async () => {
    const { data } = await axios.get(`${API}/ignored`, { params: { profile } });
    setIgnored(data);
  }, [profile]);

  const fetchDuplicates = useCallback(async () => {
    const { data } = await axios.get(`${API}/clubs/duplicates`, { params: { profile } });
    setDupGroups(data.groups || []);
  }, [profile]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchSources(); }, [fetchSources]);
  useEffect(() => { fetchIgnored(); }, [fetchIgnored]);
  useEffect(() => { fetchDuplicates(); }, [fetchDuplicates, clubs]);

  useEffect(() => {
    const measure = () => {
      const el = tableScrollRef.current;
      if (el) {
        setScrollW(el.scrollWidth);
        setOverflowX(el.scrollWidth > el.clientWidth + 1);
      } else {
        setOverflowX(false);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [clubs, isSport]);

  const handleTableScroll = () => {
    if (barRef.current && tableScrollRef.current) barRef.current.scrollLeft = tableScrollRef.current.scrollLeft;
  };
  const handleBarScroll = () => {
    if (barRef.current && tableScrollRef.current) tableScrollRef.current.scrollLeft = barRef.current.scrollLeft;
  };

  const resizableCols = useMemo(() => {
    const cols = [
      { id: "name", label: "Naam", def: 220 },
      { id: "email", label: "Contact e-mail", def: 240 },
      { id: "ctitle", label: "Functie", def: 150 },
      { id: "cname", label: "Contactpersoon", def: 190 },
    ];
    if (isSport) cols.push({ id: "sport", label: "Sport", def: 130 });
    cols.push(
      { id: "phone", label: "Telefoon", def: 140 },
      { id: "street", label: "Straat", def: 180 },
      { id: "postcode", label: "Postcode", def: 110 },
      { id: "city", label: "Plaats", def: 150 },
      { id: "board", label: "Bestuursleden", def: 260 },
      { id: "website", label: "Website", def: 190 },
      { id: "sent", label: "Verzonden", def: 170 },
      { id: "scraped", label: "Doorzocht", def: 120 },
    );
    return cols;
  }, [isSport]);

  const colW = (c) => colWidths[c.id] || c.def;

  useEffect(() => {
    localStorage.setItem("kielzog_colw", JSON.stringify(colWidths));
  }, [colWidths]);

  const startResize = (e, id, def) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = colWidths[id] || def;
    const onMove = (ev) => {
      const w = Math.max(60, startWidth + (ev.clientX - startX));
      setColWidths((prev) => ({ ...prev, [id]: w }));
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
    };
    document.body.style.cursor = "col-resize";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const resetColWidths = () => setColWidths({});
  useEffect(() => {
    const t = setTimeout(fetchClubs, 250);
    return () => clearTimeout(t);
  }, [fetchClubs]);

  const pollJob = useCallback((jobId) => {
    clearInterval(pollRef.current);
    const id = setInterval(async () => {
      if (pollRef.current !== id) return clearInterval(id);
      try {
        const { data } = await axios.get(`${API}/scrape/status/${jobId}`);
        if (pollRef.current !== id) return;
        setJob(data);
        fetchClubs();
        fetchStats();
        if (data.status === "paused") {
          clearInterval(pollRef.current);
          toast.info(data.stage || "Gepauzeerd");
        } else if (data.status === "done") {
          clearInterval(pollRef.current);
          fetchSources();
          if (data.deep && data.total > 0) { setReport(data); setReportOpen(true); }
          const skip = data.skipped ? `, ${data.skipped} overgeslagen` : "";
          toast.success((data.deep || data.mode === "retry" || data.mode === "import")
            ? (data.stage || "Taak klaar")
            : `Scrape klaar - ${data.clubs_with_email} met e-mail gevonden${skip}`);
          setTimeout(() => setJob(null), 2500);
        } else if (data.status === "error") {
          clearInterval(pollRef.current);
          toast.error(data.stage || "Scrape mislukt");
          setTimeout(() => setJob(null), 3000);
        }
      } catch (e) {
        if (e?.response?.status !== 404) return;
        clearInterval(pollRef.current);
        const { data } = await axios.get(`${API}/scrape/deep/current`).catch(() => ({ data: {} }));
        setJob(data.job || null);
      }
    }, 1200);
    pollRef.current = id;
  }, [fetchClubs, fetchStats]);
  const pollJobRef = useRef(pollJob);
  pollJobRef.current = pollJob;

  const openReport = async () => {
    const { data } = await axios.get(`${API}/scrape/deep/report`);
    if (!data.job) return toast.info("Nog geen batch-scrape uitgevoerd");
    setReport(data.job);
    setReportOpen(true);
  };
  const downloadReport = async () => {
    try {
      const res = await axios.get(`${API}/scrape/deep/report.xlsx`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `mislukte_sites_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download mislukt"); }
  };

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/scrape/deep/current`).then(({ data }) => {
      if (cancelled || !data.job) return;
      setJob(data.job);
      if (data.job.status === "running") pollJobRef.current(data.job.id);
    }).catch(() => {});
    return () => { cancelled = true; clearInterval(pollRef.current); pollRef.current = null; };
  }, []);

  const deepAction = async (action) => {
    try {
      const { data } = await axios.post(`${API}/scrape/deep/${action}`);
      if (action === "resume") {
        setJob((j) => ({ ...j, status: "running", stage: "Hervatten..." }));
        pollJob(data.job_id);
      } else if (action === "pause") {
        setJob(data);
      } else {
        setJob(null);
        toast.success("Batch-scrape gestopt");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Actie mislukt");
    }
  };

  const startScrape = async ({ urls = [], directory = true, follow = false } = {}) => {
    if (job && job.status === "running") return;
    try {
      const { data } = await axios.post(`${API}/scrape/start`, {
        use_directory: directory,
        custom_urls: urls,
        follow_links: follow,
        skip_scraped: skipScraped,
        profile,
        limit: 100,
      });
      toast.info("Scrape gestart...");
      setJob({ status: "running", stage: "Starten...", processed: 0, total: 0 });
      pollJob(data.job_id);
    } catch (e) {
      const msg = e?.response?.status === 409 ? (e.response.data?.detail || "Er loopt al een scrape-taak") : "Kon scrape niet starten";
      toast.error(msg);
    }
  };

  const runCustom = () => {
    const urls = customUrls.split(/[\n,]+/).map((u) => u.trim()).filter(Boolean);
    if (!urls.length) return toast.error("Geen URL's opgegeven");
    setCustomOpen(false);
    setCustomUrls("");
    startScrape({ urls, directory: useDirectory && hasDirectory, follow: false });
  };

  const runSources = () => {
    const urls = sources.split(/[\n,]+/).map((u) => u.trim()).filter(Boolean);
    if (!urls.length) return toast.error("Geef minimaal één bron op");
    startScrape({ urls, directory: false, follow: followLinks });
  };

  const forgetSource = async (src) => {
    await axios.delete(`${API}/sources`, { params: { url: src.url, profile } });
    fetchSources();
    toast.success("Bron verwijderd — kan opnieuw doorzocht worden");
  };

  const isIncomplete = (c) => {
    const addr = c.street || c.postcode;
    const hasBoard = Array.isArray(c.board) ? c.board.length > 0 : !!c.board;
    return !(c.emails && c.emails.length) || !c.phone || !hasBoard || !addr;
  };

  const findEmailsFor = async (ids) => {
    try {
      const { data } = await axios.post(`${API}/scrape/retry-ids`, { ids });
      toast.info(`E-mails zoeken voor ${ids.length} nieuwe leads...`);
      setJob({ status: "running", stage: "Starten...", processed: 0, total: ids.length });
      pollJob(data.job_id);
    } catch (e) {
      toast.error(e?.response?.status === 409 ? (e.response.data?.detail || "Er loopt al een scrape-taak") : "Kon niet starten");
    }
  };

  const moveSelected = async (target) => {
    const label = PROFILES.find((p) => p.id === target)?.label || target;
    if (!window.confirm(`${selectedIds.length} lead(s) verplaatsen naar "${label}"?\n\nCRM-status en verzendhistorie gaan mee.`)) return;
    try {
      const { data } = await axios.post(`${API}/clubs/move`, { ids: selectedIds, profile: target });
      toast.success(`${data.moved} lead(s) verplaatst naar ${label}`);
      setSelectedIds([]);
      fetchClubs(); fetchStats();
    } catch { toast.error("Verplaatsen mislukt"); }
  };


  const huntPersonal = async () => {
    if (job && job.status === "running") return;
    const n = stats.generic_emails || 0;
    if (!n) return toast.info("Geen algemene adressen om te verbeteren");
    try {
      const { data } = await axios.post(`${API}/scrape/hunt-personal`, null, { params: { profile } });
      toast.info(`Zoeken naar persoonlijke adressen bij ${n} leads...`);
      setJob({ status: "running", stage: "Starten...", processed: 0, total: n });
      pollJob(data.job_id);
    } catch (e) {
      const msg = e?.response?.status === 409 ? (e.response.data?.detail || "Er loopt al een scrape-taak") : "Kon niet starten";
      toast.error(msg);
    }
  };

  const retryMissing = async () => {
    if (job && job.status === "running") return;
    const missing = clubs.filter(isIncomplete).length;
    if (!missing) return toast.info("Alle organisaties zijn al compleet");
    try {
      const { data } = await axios.post(`${API}/scrape/retry-missing`, null, { params: { profile } });
      toast.info(`Opnieuw scrapen van ${missing} met ontbrekende gegevens...`);
      setJob({ status: "running", stage: "Starten...", processed: 0, total: missing });
      pollJob(data.job_id);
    } catch (e) {
      const msg = e?.response?.status === 409 ? (e.response.data?.detail || "Er loopt al een scrape-taak") : "Kon niet starten";
      toast.error(msg);
    }
  };

  const exportExcel = async (includeEmpty = false) => {
    try {
      const res = await axios.get(`${API}/export`, {
        params: { profile, include_empty: includeEmpty },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${cfg.id}${includeEmpty ? "_alles" : ""}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(includeEmpty ? "Excel (alles) gedownload" : "Excel (leads) gedownload");
    } catch (e) {
      toast.error("Export mislukt");
    }
  };

  const clearAll = async () => {
    if (!window.confirm(`Weet je zeker dat je alle verzamelde ${noun} wilt verwijderen?`)) return;
    await axios.delete(`${API}/clubs`, { params: { profile } });
    fetchClubs();
    fetchStats();
    toast.success("Lijst geleegd");
  };

  const deleteClub = async (club) => {
    if (!window.confirm(`"${club.name}" definitief verwijderen en op de negeerlijst zetten?`)) return;
    try {
      await axios.delete(`${API}/clubs/${club.id}`);
      setSelectedIds((prev) => prev.filter((x) => x !== club.id));
      fetchClubs();
      fetchStats();
      fetchIgnored();
      toast.success("Verwijderd — komt niet meer terug bij nieuwe scrapes");
    } catch (e) {
      toast.error("Verwijderen mislukt");
    }
  };

  const unIgnore = async (item) => {
    await axios.delete(`${API}/ignored`, { params: { domain: item.domain, profile } });
    fetchIgnored();
    toast.success("Van negeerlijst gehaald — kan opnieuw gevonden worden");
  };

  const copyAll = () => {
    const emails = [...new Set(clubs.flatMap((c) => c.emails))].join(", ");
    if (!emails) return toast.error("Geen e-mails om te kopiëren");
    const done = () => toast.success("Alle e-mails gekopieerd");
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(emails).then(done).catch(() => fallbackCopy(emails, done));
    } else {
      fallbackCopy(emails, done);
    }
  };

  const fallbackCopy = (text, done) => {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      done();
    } catch (e) {
      toast.error("Kopiëren niet ondersteund in deze browser");
    }
  };

  const isRunning = job && job.status === "running";
  const missingCount = clubs.filter(isIncomplete).length;
  const progress = job && job.total ? Math.round((job.processed / job.total) * 100) : 0;
  const lastScrape = stats.last_scrape
    ? new Date(stats.last_scrape).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" })
    : "-";

  return (
    <div className="min-h-screen flex bg-slate-50 text-slate-900">
      <Toaster position="top-right" richColors />
      <datalist id="sport-options">
        {["Voetbal","Hockey","Tennis","Padel","Korfbal","Handbal","Volleybal","Basketbal","Tafeltennis","Badminton","Zwemmen","Waterpolo","Atletiek","Gymnastiek","Vechtsport","Paardensport","Golf","Rugby","Honk- en softbal","Cricket","Wielrennen","Roeien","Kanoën","Watersport","Schaatsen","Darts","Biljart","Boogschieten","Jeu de boules","Schaken"].map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col bg-slate-950 text-slate-100 grain-texture fixed h-screen">
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-md bg-[#FF4500] flex items-center justify-center">
              <Anchor className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <div>
              <div className="font-display font-bold text-lg leading-none">Kielzog</div>
              <div className="text-[10px] tracking-[0.15em] uppercase text-slate-400 mt-1">
                Email Harvester
              </div>
            </div>
          </div>
        </div>
        <nav className="p-4 flex flex-col gap-1 text-sm">
          <div className="text-[10px] tracking-[0.15em] uppercase text-slate-500 px-3 pb-2">
            Zoekprofiel
          </div>
          {PROFILES.map((p) => {
            const Icon = p.icon;
            const active = profile === p.id;
            return (
              <button
                key={p.id}
                data-testid={`profile-${p.id}`}
                onClick={() => switchProfile(p.id)}
                className={`px-3 py-2 rounded-md flex items-center gap-3 font-medium text-left transition-colors ${active ? "bg-slate-800/70 text-white" : "text-slate-400 hover:text-white hover:bg-slate-900"}`}
              >
                <Icon className="w-4 h-4 shrink-0" strokeWidth={1.5} /> {p.label}
              </button>
            );
          })}
          <button
            data-testid="profiles-manage-button"
            onClick={() => { loadProfiles(); setProfilesOpen(true); }}
            className="mt-2 px-3 py-2 rounded-md flex items-center gap-3 text-left text-slate-500 hover:text-white hover:bg-slate-900 transition-colors"
          >
            <Settings2 className="w-4 h-4 shrink-0" strokeWidth={1.5} /> Profielen beheren
          </button>
          <ProfilesManager
            open={profilesOpen}
            onOpenChange={setProfilesOpen}
            profiles={PROFILES}
            onChanged={loadProfiles}
            onDeleted={(id) => { if (id === profile) switchProfile(PROFILES.find((p) => p.id !== id)?.id || "watersport"); }}
          />
        </nav>
        <div className="mt-auto p-4">
          <div className="rounded-lg overflow-hidden border border-slate-800 relative">
            <img src={MARINA_IMG} alt="marina" className="w-full h-28 object-cover opacity-80" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 to-transparent" />
            <p className="absolute bottom-2 left-3 right-3 text-[11px] text-slate-300 leading-snug">
              Verzamel per profiel één contact-e-mail per organisatie.
            </p>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 md:ml-64 min-w-0">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 px-6 md:px-10 py-6 sticky top-0 z-20">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 data-testid="page-title" className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
                {cfg.label}
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                {hasDirectory
                  ? "Scrape e-mailadressen live uit de directory en eigen bronnen."
                  : "Geef bronnen op en scrape live één contactadres per organisatie."}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                data-testid="sync-snapshot-button"
                variant="ghost"
                onClick={syncSnapshot}
                disabled={syncing}
                className="rounded-full text-slate-500 hover:text-[#FF4500]"
                title="Ontbrekende leads uit de ingebouwde dataset toevoegen (veilig, zonder dubbelen)"
              >
                <Combine className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> {syncing ? "Bezig..." : "Synchroniseren"}
              </Button>
              <WorkflowGuide open={guideOpen} onOpenChange={setGuideOpen} />
              <Button
                data-testid="logout-button"
                variant="ghost"
                onClick={onLogout}
                className="rounded-full text-slate-500 hover:text-slate-900"
                title="Uitloggen"
              >
                <LogOut className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Uitloggen
              </Button>
              <Dialog open={customOpen} onOpenChange={setCustomOpen}>
                <DialogTrigger asChild>
                  <Button
                    data-testid="add-url-button"
                    variant="outline"
                    className="rounded-full border-slate-300 hover:-translate-y-0.5 transition-transform"
                  >
                    <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> URL’s toevoegen
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white">
                  <DialogHeader>
                    <DialogTitle className="font-display text-xl">Eigen URL’s scrapen</DialogTitle>
                    <DialogDescription>
                      Plak losse website-adressen ({cfg.label.toLowerCase()}), één per regel of komma-gescheiden.
                    </DialogDescription>
                  </DialogHeader>
                  <Textarea
                    data-testid="custom-urls-input"
                    value={customUrls}
                    onChange={(e) => setCustomUrls(e.target.value)}
                    placeholder={cfg.example}
                    className="min-h-[140px] font-mono text-sm"
                  />
                  {hasDirectory && (
                    <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid="include-directory-checkbox"
                        checked={useDirectory}
                        onChange={(e) => setUseDirectory(e.target.checked)}
                        className="accent-[#FF4500] w-4 h-4"
                      />
                      Ook de standaard directory meenemen
                    </label>
                  )}
                  <DialogFooter>
                    <Button
                      data-testid="run-custom-scrape-button"
                      onClick={runCustom}
                      className="rounded-full bg-[#FF4500] hover:bg-[#E63E00] text-white"
                    >
                      <Play className="w-4 h-4 mr-1.5" strokeWidth={2} /> Scrapen
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {hasDirectory && (
                <Button
                  data-testid="scrape-trigger-button"
                  onClick={() => startScrape({ urls: [], directory: true })}
                  disabled={isRunning}
                  className={`rounded-full bg-[#FF4500] hover:bg-[#E63E00] text-white font-semibold px-6 hover:-translate-y-0.5 transition-transform ${isRunning ? "pulse-ring" : ""}`}
                >
                  {isRunning ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Bezig...</>
                  ) : (
                    <><Play className="w-4 h-4 mr-2" strokeWidth={2} /> Start scrape</>
                  )}
                </Button>
              )}
            </div>
          </div>
        </header>

        <div className="px-6 md:px-10 py-8 max-w-[1400px]">
          {/* Stats */}
          <TodayBar clubs={clubs} onPick={pickToday} />
          <WeekOverview profile={profile} refreshKey={clubs.map((c) => (c.crm || {}).status || "").join("")} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard icon={Building2} label="Gevonden" value={stats.total_clubs} accent="text-slate-400" />
            <StatCard icon={CheckCircle2} label="Met e-mail" value={stats.clubs_with_email} accent="text-emerald-500" />
            <StatCard icon={Mail} label="Contact e-mails" value={stats.total_emails} accent="text-[#FF4500]" />
            <StatCard icon={Clock} label="Laatste scrape" value={<span className="text-lg font-semibold">{lastScrape}</span>} accent="text-slate-400" />
          </div>

          {showQuickstart && (
            <div data-testid="quickstart-card" className="relative bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-lg p-6 md:p-7 mb-6 overflow-hidden">
              <button
                data-testid="quickstart-dismiss"
                onClick={() => { localStorage.setItem("mjp_quickstart_hidden", "1"); setShowQuickstart(false); }}
                className="absolute top-3 right-3 text-slate-400 hover:text-white transition-colors"
                title="Verberg snelstart"
              >
                <XCircle className="w-5 h-5" strokeWidth={1.5} />
              </button>
              <h2 className="font-display text-xl font-bold mb-1">Snelstart</h2>
              <p className="text-sm text-slate-300 mb-6">In 3 stappen van leads naar verzonden mails.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { n: 1, icon: Search, title: "Scrapen", desc: "Verzamel e-mailadressen van verenigingen.", cta: "Scrapen", test: "quickstart-scrape", onClick: scrollToSources },
                  { n: 2, icon: ListPlus, title: "Inschrijven", desc: "Zet leads in de 3-mail campagne.", cta: "Inschrijven", test: "quickstart-enroll", onClick: () => setCrmOpen(true) },
                  { n: 3, icon: Mail, title: "Verzendhulp", desc: "Verstuur vanuit je eigen mailaccount.", cta: "Verzendhulp", test: "quickstart-helper", onClick: () => setCrmOpen(true) },
                ].map((s, i) => (
                  <div key={s.n} className="relative rounded-lg bg-white/5 border border-white/10 p-4 flex flex-col">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#FF4500] text-white text-sm font-bold">{s.n}</span>
                      <s.icon className="w-4 h-4 text-slate-300" strokeWidth={1.5} />
                      <span className="font-display font-bold">{s.title}</span>
                    </div>
                    <p className="text-xs text-slate-400 mb-4 flex-1">{s.desc}</p>
                    <Button
                      data-testid={s.test}
                      size="sm"
                      onClick={s.onClick}
                      className="rounded-full bg-white text-slate-900 hover:bg-slate-200 w-full"
                    >
                      {s.cta}
                    </Button>
                  </div>
                ))}
              </div>
              <button
                data-testid="quickstart-guide-link"
                onClick={() => setGuideOpen(true)}
                className="mt-5 inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white underline underline-offset-4 transition-colors"
              >
                <BookOpen className="w-4 h-4" strokeWidth={1.5} /> Lees de volledige uitleg van het werkproces
              </button>
            </div>
          )}

          <div data-testid="sources-panel" className="bg-white border border-slate-200 rounded-lg p-6 mb-6">
            <div className="flex items-center gap-2 mb-1">
              <Compass className="w-5 h-5 text-[#FF4500]" strokeWidth={1.5} />
              <h2 className="font-display text-xl font-bold tracking-tight">Bronnen opgeven</h2>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Standaardbronnen voor dit profiel zijn alvast ingevuld — pas ze aan of klik direct op <span className="font-medium text-slate-700">Scrape bronnen</span>.
              Plak losse websites, óf overzichtspagina’s en vink “volg de links” aan. Voorkeursadres: <span className="font-medium text-slate-700">{cfg.priority}</span>.
            </p>
            <Textarea
              data-testid="sources-input"
              value={sources}
              onChange={(e) => setSources(e.target.value)}
              placeholder={"https://gemeente.nl/verenigingen-overzicht\n" + cfg.example}
              className="min-h-[110px] font-mono text-sm mb-3"
            />
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="follow-links-checkbox"
                    checked={followLinks}
                    onChange={(e) => setFollowLinks(e.target.checked)}
                    className="accent-[#FF4500] w-4 h-4"
                  />
                  <Link2 className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
                  Overzichts-/bronpagina’s — volg de links
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="skip-scraped-checkbox"
                    checked={skipScraped}
                    onChange={(e) => setSkipScraped(e.target.checked)}
                    className="accent-[#FF4500] w-4 h-4"
                  />
                  <CheckCircle2 className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
                  Sla al doorzochte over
                </label>
              </div>
              <Button
                data-testid="run-sources-button"
                onClick={runSources}
                disabled={isRunning}
                className={`rounded-full bg-[#FF4500] hover:bg-[#E63E00] text-white font-semibold px-6 hover:-translate-y-0.5 transition-transform ${isRunning ? "pulse-ring" : ""}`}
              >
                {isRunning ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Bezig...</>
                ) : (
                  <><ListPlus className="w-4 h-4 mr-2" strokeWidth={2} /> Scrape bronnen</>
                )}
              </Button>
            </div>

            {searchedSources.length > 0 && (
              <div data-testid="searched-sources" className="mt-5 border-t border-slate-100 pt-4">
                <div className="text-xs font-semibold tracking-[0.08em] uppercase text-slate-500 mb-2">
                  Eerder doorzochte bronnen ({searchedSources.length})
                </div>
                <ul className="flex flex-col gap-1.5">
                  {searchedSources.map((s) => (
                    <li key={s.url} data-testid={`source-${s.url}`} className="flex items-center justify-between gap-3 text-sm bg-slate-50 rounded-md px-3 py-2">
                      <div className="min-w-0 flex items-center gap-2">
                        <Compass className="w-3.5 h-3.5 text-slate-400 shrink-0" strokeWidth={1.5} />
                        <span className="truncate text-slate-700">{s.url}</span>
                      </div>
                      <div className="flex items-center gap-3 shrink-0 text-slate-400 text-xs">
                        <span className="text-emerald-600 font-semibold">{s.leads} leads</span>
                        <span>{s.links_found} links</span>
                        <span>{s.last_crawled ? new Date(s.last_crawled).toLocaleDateString("nl-NL") : ""}</span>
                        <button
                          data-testid={`forget-source-${s.url}`}
                          onClick={() => forgetSource(s)}
                          title="Vergeten (opnieuw doorzoekbaar maken)"
                          className="hover:text-red-600"
                        >
                          <XCircle className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
                <p className="text-[11px] text-slate-400 mt-2">
                  Deze bronnen worden bij "Sla al doorzochte over" niet opnieuw gecrawld. Klik op × om een bron bewust opnieuw te doorzoeken.
                </p>
              </div>
            )}
          </div>

          {/* Negeerlijst */}
          {ignored.length > 0 && (
            <div data-testid="ignored-panel" className="bg-white border border-slate-200 rounded-lg p-6 mb-6">
              <div className="flex items-center gap-2 mb-1">
                <Trash2 className="w-5 h-5 text-slate-500" strokeWidth={1.5} />
                <h2 className="font-display text-xl font-bold tracking-tight">Negeerlijst ({ignored.length})</h2>
              </div>
              <p className="text-sm text-slate-500 mb-4">
                Deze verenigingen zijn handmatig verwijderd en worden bij nieuwe scrapes automatisch overgeslagen.
                Klik op × om een organisatie weer toe te laten.
              </p>
              <ul className="flex flex-col gap-1.5">
                {ignored.map((it) => (
                  <li key={it.domain} data-testid={`ignored-${it.domain}`} className="flex items-center justify-between gap-3 text-sm bg-slate-50 rounded-md px-3 py-2">
                    <div className="min-w-0 flex items-center gap-2">
                      <Globe className="w-3.5 h-3.5 text-slate-400 shrink-0" strokeWidth={1.5} />
                      <span className="truncate text-slate-700">{it.name || it.domain}</span>
                      <span className="truncate text-slate-400 text-xs">{it.domain}</span>
                    </div>
                    <button
                      data-testid={`unignore-${it.domain}`}
                      onClick={() => unIgnore(it)}
                      title="Weer toelaten (van negeerlijst halen)"
                      className="text-slate-400 hover:text-emerald-600 shrink-0"
                    >
                      <XCircle className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Progress */}
          <AnimatePresence>
            {job && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                data-testid="scrape-progress"
                className="fixed bottom-4 right-4 z-50 w-[min(560px,calc(100vw-2rem))] bg-slate-950 text-white rounded-lg p-5 overflow-hidden shadow-2xl"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium flex items-center gap-2">
                    {job.status === "done" ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    ) : job.status === "paused" ? (
                      <Pause className="w-4 h-4 text-amber-400" />
                    ) : (
                      <Loader2 className="w-4 h-4 animate-spin text-[#FF4500]" />
                    )}
                    <span data-testid="scrape-progress-stage">{job.stage}</span>
                  </span>
                  <span className="flex items-center gap-3">
                    {job.deep && job.status === "running" && !job.pause_requested && (
                      <button data-testid="deep-pause-button" onClick={() => deepAction("pause")}
                        className="text-xs px-3 py-1 rounded-full border border-slate-600 hover:bg-slate-800 transition-colors flex items-center gap-1">
                        <Pause className="w-3 h-3" /> Pauzeer
                      </button>
                    )}
                    {job.deep && job.status === "paused" && (
                      <>
                        <button data-testid="deep-resume-button" onClick={() => deepAction("resume")}
                          className="text-xs px-3 py-1 rounded-full bg-[#FF4500] hover:bg-[#e03d00] transition-colors flex items-center gap-1">
                          <Play className="w-3 h-3" /> Hervat
                        </button>
                        <button data-testid="deep-cancel-button" onClick={() => deepAction("cancel")}
                          className="text-xs px-3 py-1 rounded-full border border-slate-600 hover:bg-slate-800 transition-colors">
                          Stoppen
                        </button>
                      </>
                    )}
                    <span data-testid="scrape-progress-count" className="text-sm text-slate-400 tabular-nums">
                      {job.deep && job.batches ? `batch ${job.batch}/${job.batches} · ` : ""}
                      {job.processed}/{job.total || "?"}
                    </span>
                  </span>
                </div>
                <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-[#FF4500]"
                    animate={{ width: `${progress}%` }}
                    transition={{ ease: "easeOut" }}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={1.5} />
              <Input
                data-testid="search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Zoek op naam, plaats, domein of e-mail..."
                className="pl-9 bg-white border-slate-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
            <Button
              data-testid="filter-email-button"
              variant={onlyEmail ? "default" : "outline"}
              onClick={() => { setOnlyEmail((v) => !v); setNoEmail(false); }}
              className={`rounded-full ${onlyEmail ? "bg-slate-900" : "border-slate-300"}`}
            >
              <Mail className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Alleen met e-mail
            </Button>
            <Button
              data-testid="filter-no-email-button"
              variant={noEmail ? "default" : "outline"}
              onClick={() => { setNoEmail((v) => !v); setOnlyEmail(false); }}
              className={`rounded-full ${noEmail ? "bg-slate-900" : "border-slate-300"}`}
              title="Toon leads zonder e-mailadres, voor handmatig nazoeken"
            >
              <MailQuestion className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Zonder e-mail ({Math.max(0, (stats.total_clubs || 0) - (stats.clubs_with_email || 0))})
            </Button>
            <SentFilterBar value={sentFilter} onChange={setSentFilter} counts={sentCounts} />
            {missingCount > 0 && (
              <Button
                data-testid="retry-missing-button"
                variant="outline"
                onClick={retryMissing}
                disabled={isRunning}
                className="rounded-full border-[#FF4500] text-[#FF4500] hover:bg-orange-50"
              >
                <MailQuestion className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                Zoek ontbrekende gegevens ({missingCount})
              </Button>
            )}
            {stats.generic_emails > 0 && (
              <Button
                data-testid="hunt-personal-button"
                variant="outline"
                onClick={huntPersonal}
                disabled={isRunning}
                className="rounded-full border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                title="Her-scrape leads met alleen een algemeen adres (info@) om een persoonlijk rol-adres (penningmeester@/voorzitter@) te vinden"
              >
                <User className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                Zoek persoonlijke adressen ({stats.generic_emails})
              </Button>
            )}
            <Button
              data-testid="open-scrape-report-button"
              variant="outline"
              onClick={openReport}
              className="rounded-full"
            >
              <FileBarChart className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
              Laatste scrape-rapport
            </Button>
            <ScrapeSettings api={API} />
            <ScrapeReport open={reportOpen} onOpenChange={setReportOpen} job={report} onDownload={downloadReport} />
            {selectedIds.length >= 1 && (
              <MoveSelect profiles={PROFILES} current={profile} count={selectedIds.length} onMove={moveSelected} />
            )}
            {selectedIds.length >= 1 && (
              <Button
                data-testid="batch-send-button"
                onClick={startBatch}
                className="rounded-full bg-[#FF4500] hover:bg-[#E63E00] text-white"
              >
                <Mail className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                Verzendhulp voor selectie ({selectedIds.length})
              </Button>
            )}
            {selectedIds.length >= 1 && (
              <div className="inline-flex items-center rounded-full border border-[#FF4500] overflow-hidden">
                <select
                  data-testid="batch-csv-step-select"
                  value={exportStepSel}
                  onChange={(e) => setExportStepSel(Number(e.target.value))}
                  title="Welke mail exporteer je? Alleen contacten die aan de beurt zijn komen in de CSV."
                  className="h-9 pl-3 pr-1 text-sm font-medium text-[#FF4500] bg-orange-50 border-r border-[#FF4500] outline-none cursor-pointer"
                >
                  <option value={1}>Mail 1</option>
                  <option value={2}>Mail 2</option>
                  <option value={3}>Mail 3</option>
                </select>
                <button
                  data-testid="batch-csv-button"
                  onClick={exportSelectionCsv}
                  className="h-9 px-3 inline-flex items-center text-sm font-medium text-[#FF4500] hover:bg-orange-50 transition-colors"
                >
                  <Download className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                  CSV voor selectie ({selectedIds.length})
                </button>
              </div>
            )}
            {selectedIds.length >= 1 && (
              <ScheduleSelection API={API} ids={selectedIds} onDone={() => { fetchClubs(); }} />
            )}
            {selectedIds.length >= 2 && selectedIds.length <= 10 && (
              <Button
                data-testid="merge-button"
                variant="outline"
                onClick={openMerge}
                className="rounded-full border-blue-500 text-blue-600 hover:bg-blue-50"
              >
                <Combine className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                Samenvoegen ({selectedIds.length})
              </Button>
            )}
            {dupGroups.length > 0 && (
              <Button
                data-testid="duplicates-button"
                variant="outline"
                onClick={() => setDupOpen(true)}
                className="rounded-full border-amber-500 text-amber-600 hover:bg-amber-50"
                title="Waarschijnlijke dubbele records met dezelfde naam maar een ander domein"
              >
                <GitMerge className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                Mogelijke duplicaten ({dupGroups.length})
              </Button>
            )}
            <Button data-testid="copy-emails-button" variant="outline" onClick={copyAll} className="rounded-full border-slate-300">
              <Copy className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Kopieer e-mails
            </Button>
            <Button data-testid="export-button" variant="outline" onClick={() => exportExcel(false)} className="rounded-full border-slate-300">
              <Download className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Excel (leads)
            </Button>
            <Button data-testid="export-all-button" variant="ghost" onClick={() => exportExcel(true)} className="rounded-full text-slate-500 hover:text-slate-900">
              <Download className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Alles
            </Button>
            <Button data-testid="import-button" variant="outline" onClick={() => setImportOpen(true)} className="rounded-full border-slate-300">
              <Upload className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Excel uploaden
            </Button>
            <Button data-testid="snapshots-button" variant="ghost" onClick={() => setSnapsOpen(true)} className="rounded-full text-slate-500 hover:text-slate-900">
              <History className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Herstelpunten
            </Button>
            <LeadsImport open={importOpen} onOpenChange={setImportOpen} profile={profile} onDone={() => { fetchClubs(); fetchStats(); }} onFindEmails={findEmailsFor} />
            <MapToExcel mode="pdf" open={pdfImportOpen} onOpenChange={setPdfImportOpen} profile={profile} profileLabel={cfg.label} onOpenUpload={() => setImportOpen(true)} />
            <MapToExcel open={mapImportOpen} onOpenChange={setMapImportOpen} profile={profile} profileLabel={cfg.label} onOpenUpload={() => setImportOpen(true)} />
            <Snapshots open={snapsOpen} onOpenChange={setSnapsOpen} profile={profile} onDone={() => { fetchClubs(); fetchStats(); }} />
            <ChangeLog club={historyClub} onClose={() => setHistoryClub(null)} />
            <Button data-testid="crm-open-button" variant="outline" onClick={() => setCrmOpen(true)} className="rounded-full border-[#FF4500] text-[#FF4500] hover:bg-orange-50">
              <Mail className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> CRM &amp; mailen
            </Button>
            <Button
              data-testid="map-import-button"
              variant="outline"
              onClick={() => setMapImportOpen(true)}
              className="rounded-full border-emerald-500 text-emerald-600 hover:bg-emerald-50"
              title="Plak de link van een kaart (Google My Maps) of kies een KML/KMZ-bestand en download de punten als Excel"
            >
              <MapPin className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Importeer KML-kaart
            </Button>
            <Button
              data-testid="pdf-import-button"
              variant="outline"
              onClick={() => setPdfImportOpen(true)}
              className="rounded-full border-emerald-500 text-emerald-600 hover:bg-emerald-50"
              title="Lees een pdf met verenigingen uit (naam, adres, link achter 'Website') en download als Excel"
            >
              <FileText className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Importeer PDF
            </Button>
            {profile === "watersport" && (
              <Button
                data-testid="kml-button"
                variant="outline"
                onClick={() => { window.open(`${API}/verenigingen-kml`, "_blank", "noopener"); toast.info("KML wordt gegenereerd — open het in Google Earth"); }}
                className="rounded-full border-blue-500 text-blue-600 hover:bg-blue-50"
                title="Download alle watersportverenigingen als KML voor Google Earth"
              >
                <MapPin className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> KML (Google Earth)
              </Button>
            )}
            <Button data-testid="clear-button" variant="ghost" onClick={clearAll} className="rounded-full text-slate-500 hover:text-red-600">
              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          </div>

          {/* Table */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            {displayClubs.length === 0 ? (
              <div data-testid="empty-state" className="py-20 flex flex-col items-center text-center px-6">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                  <Anchor className="w-7 h-7 text-slate-400" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-xl font-bold mb-1">{clubs.length > 0 && (sentFilter || noEmail) ? "Geen contacten voor dit filter" : "Nog geen resultaten"}</h3>
                <p className="text-sm text-slate-500 max-w-sm">
                  {clubs.length > 0 && (sentFilter || noEmail)
                    ? <>Zet het filter uit om alle contacten weer te zien.</>
                    : hasDirectory
                    ? <>Klik op <span className="text-[#FF4500] font-semibold">Start scrape</span> of geef bronnen op om live e-mailadressen te verzamelen.</>
                    : <>Geef hierboven bronnen op en klik op <span className="text-[#FF4500] font-semibold">Scrape bronnen</span> om te beginnen.</>}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto" ref={tableScrollRef} onScroll={handleTableScroll}>
              <table className="text-sm" style={{ tableLayout: "fixed", width: 52 + 44 + resizableCols.reduce((s, c) => s + colW(c), 0) }}>
                <colgroup>
                  <col style={{ width: 52 }} />
                  <col style={{ width: 44 }} />
                  {resizableCols.map((c) => (
                    <col key={c.id} style={{ width: colW(c) }} />
                  ))}
                </colgroup>
                <thead>
                  <tr className="border-b border-slate-200 text-left">
                    <th className="px-4 py-3"></th>
                    <th className="px-4 py-3">
                      <input
                        type="checkbox"
                        data-testid="select-all-checkbox"
                        title="Selecteer alle zichtbare contacten"
                        checked={displayClubs.length > 0 && displayClubs.every((c) => selectedIds.includes(c.id))}
                        onChange={(e) => {
                          const vis = displayClubs.map((c) => c.id);
                          setSelectedIds((prev) => e.target.checked
                            ? Array.from(new Set([...prev, ...vis]))
                            : prev.filter((id) => !vis.includes(id)));
                        }}
                        className="accent-[#FF4500] w-4 h-4 cursor-pointer"
                      />
                    </th>
                    {resizableCols.map((c) => (
                      <th
                        key={c.id}
                        className="relative px-6 py-3 text-xs font-semibold tracking-[0.08em] uppercase text-slate-500 select-none"
                      >
                        {c.id === "sent" ? (
                          <button
                            data-testid="sort-sent-button"
                            onClick={toggleSentSort}
                            className="inline-flex items-center gap-1 uppercase tracking-[0.08em] hover:text-slate-900"
                            title="Sorteer op verzendmoment"
                          >
                            {c.label}
                            {sentSort === "asc" ? <ArrowUp className="w-3 h-3" /> : sentSort === "desc" ? <ArrowDown className="w-3 h-3" /> : <ArrowUpDown className="w-3 h-3 opacity-50" />}
                          </button>
                        ) : c.id === "email" ? (
                          <button
                            data-testid="sort-kind-button"
                            onClick={toggleKindSort}
                            className="inline-flex items-center gap-1 uppercase tracking-[0.08em] hover:text-slate-900"
                            title="Sorteer: Persoonlijk → Algemeen → Geen contact → uit"
                          >
                            {c.label}
                            {kindSort ? (
                              <span data-testid="sort-kind-label" className="normal-case tracking-normal rounded-full bg-[#FF4500]/10 text-[#FF4500] px-1.5 py-0.5 text-[10px] font-semibold">{KIND_LABEL[kindSort]}</span>
                            ) : <ArrowUpDown className="w-3 h-3 opacity-50" />}
                          </button>
                        ) : c.label}
                        <span
                          data-testid={`resize-${c.id}`}
                          onMouseDown={(e) => startResize(e, c.id, c.def)}
                          className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-[#FF4500]/50 active:bg-[#FF4500]"
                        />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayClubs.map((c) => (
                    <tr
                      key={c.id}
                      data-testid={`club-row-${c.domain}`}
                      className={`border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors ${selectedIds.includes(c.id) ? "bg-blue-50/60" : ""}`}
                    >
                      <td className="px-4 py-4 align-top">
                        <button
                          data-testid={`delete-${c.domain}`}
                          onClick={() => deleteClub(c)}
                          title="Vereniging verwijderen"
                          className="text-slate-300 hover:text-red-600 transition-colors mt-0.5"
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                        <button
                          data-testid={`history-${c.domain}`}
                          onClick={() => setHistoryClub(c)}
                          title="Wijzigingslog bekijken"
                          className="block text-slate-300 hover:text-[#FF4500] transition-colors mt-2"
                        >
                          <History className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <input
                          type="checkbox"
                          data-testid={`select-${c.domain}`}
                          checked={selectedIds.includes(c.id)}
                          onChange={() => toggleSelect(c.id)}
                          className="accent-[#FF4500] w-4 h-4 mt-1 cursor-pointer"
                        />
                      </td>
                      <td className="px-6 py-4 font-medium text-slate-900 align-top">
                        <div className="flex items-start gap-2">
                          {c.scrape_failed
                            ? <span data-testid={`scrape-failed-icon-${c.id}`} title={`Scrape mislukt: ${c.scrape_failed.reason}`}><AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" strokeWidth={1.5} /></span>
                            : c.reachable
                            ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" strokeWidth={1.5} />
                            : <XCircle className="w-4 h-4 text-slate-300 mt-0.5 shrink-0" strokeWidth={1.5} />}
                          <span className="line-clamp-2">{c.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 align-top">
                        {editingId === c.id ? (
                          <div className="flex items-center gap-1.5">
                            <Input
                              data-testid={`email-edit-input-${c.domain}`}
                              autoFocus
                              value={editValue}
                              onChange={(ev) => setEditValue(ev.target.value)}
                              onKeyDown={(ev) => { if (ev.key === "Enter") saveEmail(c); if (ev.key === "Escape") setEditingId(null); }}
                              placeholder="naam@vereniging.nl"
                              className="h-8 text-sm w-56"
                            />
                            <Button
                              data-testid={`email-save-${c.domain}`}
                              size="sm"
                              onClick={() => saveEmail(c)}
                              className="h-8 px-2 bg-emerald-600 hover:bg-emerald-700"
                            >
                              <CheckCircle2 className="w-4 h-4" strokeWidth={2} />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} className="h-8 px-2 text-slate-400">
                              <XCircle className="w-4 h-4" strokeWidth={2} />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 group">
                            {c.emails.length ? (
                              <div className="flex flex-col gap-1">
                                {c.emails.map((e) => (
                                  <div key={e} className="flex items-center gap-1.5 w-fit">
                                    <a
                                      href={`mailto:${e}`}
                                      data-testid={`email-${e}`}
                                      className="text-[#FF4500] hover:underline break-all"
                                    >
                                      {e}
                                    </a>
                                    {addrKind(e) && (
                                      <span
                                        data-testid={`addrkind-${c.domain}`}
                                        className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${addrKind(e) === "persoonlijk" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}
                                        title={addrKind(e) === "persoonlijk" ? "Persoonlijk/rol-adres — scoort doorgaans beter" : "Algemeen adres"}
                                      >
                                        {addrKind(e) === "persoonlijk" ? "persoonlijk" : "algemeen"}
                                      </span>
                                    )}
                                  </div>
                                ))}
                                {(c.source === "handmatig" || c.source === "mini-browser") && (
                                  <span
                                    data-testid={`source-badge-${c.domain}`}
                                    className="inline-flex items-center w-fit rounded-full bg-blue-50 text-blue-700 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                                  >
                                    {c.source === "mini-browser" ? "Mini-browser" : "Handmatig"}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                            <button
                              data-testid={`email-edit-${c.domain}`}
                              onClick={() => { setEditingId(c.id); setEditValue(c.emails[0] || ""); }}
                              className="opacity-40 md:opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity text-slate-400 hover:text-slate-700"
                              title={c.emails.length ? "E-mail aanpassen" : "E-mail invullen"}
                            >
                              {c.emails.length
                                ? <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                                : <Plus className="w-4 h-4" strokeWidth={1.5} />}
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 align-top text-slate-600">
                        <EditableCell
                          value={c.crm?.contact?.title}
                          testid={`contact-title-${c.domain}`}
                          placeholder="Penningmeester"
                          width="w-28"
                          onSave={(v) => patchField(c, "contact_title", v, "Functie opgeslagen")}
                        />
                      </td>
                      <td className="px-6 py-4 align-top text-slate-600">
                        <EditableCell
                          value={c.crm?.contact?.name}
                          testid={`contact-name-${c.domain}`}
                          icon={UserRound}
                          placeholder="Jan Jansen"
                          width="w-36"
                          onSave={(v) => patchField(c, "contact_name", v, "Contactpersoon opgeslagen")}
                        />
                      </td>
                      {isSport && (
                        <td className="px-6 py-4 align-top">
                          {editingCat === c.id ? (
                            <div className="flex items-center gap-1.5">
                              <Input
                                data-testid={`sport-edit-input-${c.domain}`}
                                autoFocus
                                list="sport-options"
                                value={editCatValue}
                                onChange={(ev) => setEditCatValue(ev.target.value)}
                                onKeyDown={(ev) => { if (ev.key === "Enter") saveCategory(c); if (ev.key === "Escape") setEditingCat(null); }}
                                placeholder="bijv. Voetbal"
                                className="h-8 text-sm w-40"
                              />
                              <Button
                                data-testid={`sport-save-${c.domain}`}
                                size="sm"
                                onClick={() => saveCategory(c)}
                                className="h-8 px-2 bg-emerald-600 hover:bg-emerald-700"
                              >
                                <CheckCircle2 className="w-4 h-4" strokeWidth={2} />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 group">
                              {c.category ? (
                                <span data-testid={`sport-${c.domain}`} className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 px-2.5 py-0.5 text-xs font-medium">
                                  {c.category}
                                </span>
                              ) : <span className="text-slate-300">—</span>}
                              <button
                                data-testid={`sport-edit-${c.domain}`}
                                onClick={() => { setEditingCat(c.id); setEditCatValue(c.category || ""); }}
                                className="opacity-40 md:opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity text-slate-400 hover:text-slate-700"
                                title={c.category ? "Sport aanpassen" : "Sport invullen"}
                              >
                                {c.category
                                  ? <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                                  : <Plus className="w-4 h-4" strokeWidth={1.5} />}
                              </button>
                            </div>
                          )}
                        </td>
                      )}
                      <td className="px-6 py-4 align-top text-slate-600">
                        <EditableCell
                          value={c.phone}
                          testid={`phone-${c.domain}`}
                          icon={Phone}
                          placeholder="010-1234567"
                          width="w-36"
                          onSave={(v) => patchField(c, "phone", v, "Telefoon opgeslagen")}
                        />
                      </td>
                      <td className="px-6 py-4 align-top text-slate-600">
                        <EditableCell
                          value={c.street}
                          testid={`street-${c.domain}`}
                          icon={MapPin}
                          placeholder="Havenstraat 12"
                          width="w-44"
                          onSave={(v) => patchField(c, "street", v, "Straat opgeslagen")}
                        />
                      </td>
                      <td className="px-6 py-4 align-top text-slate-600">
                        <EditableCell
                          value={c.postcode}
                          testid={`postcode-${c.domain}`}
                          placeholder="1234 AB"
                          width="w-28"
                          onSave={(v) => patchField(c, "postcode", v, "Postcode opgeslagen")}
                        />
                      </td>
                      <td className="px-6 py-4 align-top text-slate-600">
                        <EditableCell
                          value={c.city}
                          testid={`city-${c.domain}`}
                          icon={MapPin}
                          placeholder="Amsterdam"
                          width="w-36"
                          onSave={(v) => patchField(c, "city", v, "Plaats opgeslagen")}
                        />
                      </td>
                      <td className="px-6 py-4 align-top text-slate-600">
                        <BoardCell club={c} onSave={(text) => patchField(c, "board", text, "Bestuursleden opgeslagen")} />
                      </td>
                      <td className="px-6 py-4 align-top">
                        <div className="flex items-center gap-2">
                          <a
                            href={c.website}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`website-link-${c.domain}`}
                            className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900 break-all"
                          >
                            <Globe className="w-3.5 h-3.5" strokeWidth={1.5} /> {c.domain}
                          </a>
                        </div>
                      </td>
                      <td data-testid={`sent-${c.domain}`} className="px-6 py-4 align-top whitespace-nowrap">
                        <SentCell crm={c.crm} onReply={() => markReplied(c)} onCustomer={() => markCustomer(c)} onPermission={(st) => markPermission(c, st)} testid={c.domain} />
                      </td>
                      <td data-testid={`scraped-${c.domain}`} className="px-6 py-4 align-top text-slate-500 whitespace-nowrap">
                        {c.scraped_at ? new Date(c.scraped_at).toLocaleDateString("nl-NL") : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
          {overflowX && (
            <div
              data-testid="hscroll"
              ref={barRef}
              onScroll={handleBarScroll}
              className="sticky bottom-0 z-10 overflow-x-auto bg-slate-50/95 backdrop-blur border-t border-slate-200 rounded-b-lg"
            >
              <div style={{ width: scrollW, height: 14 }} />
            </div>
          )}
          <div className="flex items-center justify-between mt-3">
            <p className="text-xs text-slate-400">
              {displayClubs.length} resultaten getoond. E-mailadressen worden alleen verzameld waar ze publiek op de website staan.
            </p>
            <button
              data-testid="reset-colwidths"
              onClick={resetColWidths}
              className="text-xs text-slate-400 hover:text-slate-700 underline decoration-dotted"
            >
              Kolombreedtes herstellen
            </button>
          </div>
        </div>

        <Crm
          open={crmOpen}
          onOpenChange={(v) => { setCrmOpen(v); if (!v) setCrmAutoHelper(false); }}
          API={API}
          profile={profile}
          profileLabel={cfg.label}
          autoHelper={crmAutoHelper}
          onAutoHelperConsumed={() => setCrmAutoHelper(false)}
          batchLeads={batchLeads}
          batchNonce={batchNonce}
        />

        <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
          <DialogContent className="bg-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Records samenvoegen</DialogTitle>
              <DialogDescription>
                Kies welk record het hoofdrecord blijft (website en bron blijven behouden). De overige records worden
                samengevoegd en daarna verwijderd. Lege velden worden aangevuld vanuit de andere records; bestuursleden
                worden gecombineerd.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-2 max-h-[340px] overflow-y-auto">
              {clubs.filter((c) => selectedIds.includes(c.id)).map((c) => (
                <label
                  key={c.id}
                  data-testid={`merge-primary-${c.domain}`}
                  className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${mergePrimary === c.id ? "border-[#FF4500] bg-orange-50" : "border-slate-200 hover:bg-slate-50"}`}
                >
                  <input
                    type="radio"
                    name="merge-primary"
                    checked={mergePrimary === c.id}
                    onChange={() => setMergePrimary(c.id)}
                    className="accent-[#FF4500] w-4 h-4 mt-0.5 shrink-0"
                  />
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">{c.name}</div>
                    <div className="text-xs text-slate-500 truncate">{c.domain}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {(c.emails && c.emails[0]) || "geen e-mail"} · {c.city || "geen plaats"}
                    </div>
                  </div>
                </label>
              ))}
            </div>
            <DialogFooter>
              <Button data-testid="cancel-merge-button" variant="ghost" onClick={() => setMergeOpen(false)} className="rounded-full">
                Annuleren
              </Button>
              <Button
                data-testid="confirm-merge-button"
                onClick={doMerge}
                className="rounded-full bg-[#FF4500] hover:bg-[#E63E00] text-white"
              >
                <Combine className="w-4 h-4 mr-1.5" strokeWidth={2} /> Samenvoegen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={dupOpen} onOpenChange={setDupOpen}>
          <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-xl flex items-center gap-2">
                <GitMerge className="w-5 h-5 text-amber-500" /> Mogelijke duplicaten
              </DialogTitle>
              <DialogDescription>
                Records met vrijwel dezelfde naam maar een afwijkend domein. Controleer en voeg met één klik samen —
                je kiest daarna welk record het hoofdrecord blijft.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3">
              {dupGroups.length === 0 && (
                <p data-testid="dup-empty" className="text-sm text-slate-400 py-6 text-center">
                  Geen mogelijke duplicaten gevonden.
                </p>
              )}
              {dupGroups.map((g) => (
                <div
                  key={g.key}
                  data-testid={`dup-group-${g.key.replace(/\s+/g, "-")}`}
                  className="border border-slate-200 rounded-lg p-3"
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                      {g.clubs.length} records
                    </span>
                    <Button
                      data-testid={`dup-merge-${g.key.replace(/\s+/g, "-")}`}
                      size="sm"
                      onClick={() => mergeGroup(g)}
                      className="rounded-full bg-amber-500 hover:bg-amber-600 text-white h-8"
                    >
                      <Combine className="w-4 h-4 mr-1.5" strokeWidth={2} /> Samenvoegen
                    </Button>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    {g.clubs.map((c) => (
                      <div key={c.id} className="flex items-baseline justify-between gap-3 text-sm">
                        <div className="min-w-0">
                          <span className="font-medium text-slate-900">{c.name}</span>
                          <span className="text-slate-400"> · {c.domain}</span>
                        </div>
                        <div className="text-xs text-slate-500 shrink-0 text-right">
                          {c.email || "geen e-mail"}{c.city ? ` · ${c.city}` : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button data-testid="dup-close-button" variant="ghost" onClick={() => setDupOpen(false)} className="rounded-full">
                Sluiten
              </Button>
              {dupGroups.length > 0 && (
                <Button
                  data-testid="dup-merge-all-button"
                  onClick={mergeAllDuplicates}
                  className="rounded-full bg-amber-500 hover:bg-amber-600 text-white"
                >
                  <Combine className="w-4 h-4 mr-1.5" strokeWidth={2} /> Alle {dupGroups.length} samenvoegen
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
